'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback } from '@extern/ui/avatar';
import { Button } from '@extern/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@extern/ui/input-otp';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import { LockKeyIcon, MailboxIcon } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useSignIn } from '../_hooks/use-sign-in';
const CodeSchema = z.object({
  code: z.string().min(6, {
    message: 'Please enter a valid code'
  })
});
const BackupCodeSchema = z.object({
  code: z.string().min(1, {
    message: 'Please enter your backup code'
  })
});
const cardClassName = 'flex w-full sm:w-[358px] max-w-full flex-col gap-6 overflow-clip rounded-3xl border border-role-outline-subtle bg-white p-6';
export function SignInVerificationsExperiment() {
  const $ = _c(85);
  const {
    onSignUpSubmitCode,
    onResendCode,
    onUseBackupCode,
    fetchStatus,
    signIn,
    errors,
    secondFactorStrategy
  } = useSignIn();
  const [resendTimeLeft, setResendTimeLeft] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      resolver: zodResolver(CodeSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const codeForm = useForm(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      resolver: zodResolver(CodeSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totpForm = useForm(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      resolver: zodResolver(BackupCodeSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const backupCodeForm = useForm(t2);
  let t3;
  let t4;
  if ($[3] !== resendTimeLeft) {
    t3 = () => {
      let timer;
      if (resendTimeLeft > 0) {
        timer = setTimeout(() => setResendTimeLeft(_temp), 1000);
      }
      return () => clearTimeout(timer);
    };
    t4 = [resendTimeLeft];
    $[3] = resendTimeLeft;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  if (secondFactorStrategy === "totp") {
    let t5;
    if ($[6] !== onSignUpSubmitCode || $[7] !== totpForm) {
      t5 = totpForm.handleSubmit(async data => {
        const ok = await onSignUpSubmitCode(data.code);
        if (!ok) {
          totpForm.reset();
        }
      });
      $[6] = onSignUpSubmitCode;
      $[7] = totpForm;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Avatar size="48"><AvatarFallback className="bg-role-info-container text-role-on-info-container" size="48" variant="icon"><LockKeyIcon size={20} /></AvatarFallback></Avatar>;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex w-full flex-col items-center justify-center gap-6 text-center">{t6}<Text body="lg" className="text-role-on-background">Enter the code from <span className="md:block">your authenticator app</span></Text></div>;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== errors.fields.code) {
      t8 = t9 => {
        const {
          field
        } = t9;
        return <FormItem><FormControl><InputOTP maxLength={6} {...field}><InputOTPGroup className="h-full w-full"><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={0} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={1} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={2} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={3} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={4} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={5} /></InputOTPGroup></InputOTP></FormControl>{errors.fields.code ? <FormMessage>{errors.fields.code.message}</FormMessage> : <FormMessage />}</FormItem>;
      };
      $[11] = errors.fields.code;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== t8 || $[14] !== totpForm.control) {
      t9 = <div className="flex flex-col gap-6"><FormField control={totpForm.control} name="code" render={t8} /></div>;
      $[13] = t8;
      $[14] = totpForm.control;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    const t10 = fetchStatus === "fetching" || totpForm.formState.isSubmitting || !totpForm.formState.isValid;
    const t11 = fetchStatus === "fetching";
    let t12;
    if ($[16] !== t10 || $[17] !== t11) {
      t12 = <div className="flex w-full gap-4"><Button appearance="filled" aria-label="Continue" className="flex-1" color="info" data-cy="sign-in" disabled={t10} isLoading={t11} type="submit">Continue</Button></div>;
      $[16] = t10;
      $[17] = t11;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    let t13;
    if ($[19] !== onUseBackupCode) {
      t13 = <Button appearance="ghost" onClick={onUseBackupCode} type="button">Use a backup code instead</Button>;
      $[19] = onUseBackupCode;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    let t14;
    if ($[21] !== t12 || $[22] !== t13 || $[23] !== t9) {
      t14 = <div className={cardClassName}>{t7}{t9}{t12}{t13}</div>;
      $[21] = t12;
      $[22] = t13;
      $[23] = t9;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    let t15;
    if ($[25] !== t14 || $[26] !== t5) {
      t15 = <form className="w-full" onSubmit={t5}>{t14}</form>;
      $[25] = t14;
      $[26] = t5;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    let t16;
    if ($[28] !== t15 || $[29] !== totpForm) {
      t16 = <Form key="totp" {...totpForm}>{t15}</Form>;
      $[28] = t15;
      $[29] = totpForm;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    return t16;
  }
  if (secondFactorStrategy === "backup_code") {
    let t5;
    if ($[31] !== backupCodeForm || $[32] !== onSignUpSubmitCode) {
      t5 = backupCodeForm.handleSubmit(async data_0 => {
        const ok_0 = await onSignUpSubmitCode(data_0.code);
        if (!ok_0) {
          backupCodeForm.reset();
        }
      });
      $[31] = backupCodeForm;
      $[32] = onSignUpSubmitCode;
      $[33] = t5;
    } else {
      t5 = $[33];
    }
    let t6;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex w-full flex-col items-center justify-center gap-6 text-center"><Text body="lg" className="text-role-on-background">Enter one of your saved backup codes to sign in</Text></div>;
      $[34] = t6;
    } else {
      t6 = $[34];
    }
    let t7;
    if ($[35] !== errors.fields.code) {
      t7 = t8 => {
        const {
          field: field_0
        } = t8;
        return <FormItem><FormControl><Input autoCapitalize="none" autoComplete="one-time-code" autoCorrect="off" data-cy="backup-code" id="backup-code" placeholder="Enter backup code" {...field_0} /></FormControl>{errors.fields.code ? <FormMessage>{errors.fields.code.message}</FormMessage> : <FormMessage />}</FormItem>;
      };
      $[35] = errors.fields.code;
      $[36] = t7;
    } else {
      t7 = $[36];
    }
    let t8;
    if ($[37] !== backupCodeForm.control || $[38] !== t7) {
      t8 = <div className="flex flex-col gap-6"><FormField control={backupCodeForm.control} name="code" render={t7} /></div>;
      $[37] = backupCodeForm.control;
      $[38] = t7;
      $[39] = t8;
    } else {
      t8 = $[39];
    }
    const t9 = fetchStatus === "fetching" || backupCodeForm.formState.isSubmitting || !backupCodeForm.formState.isValid;
    const t10 = fetchStatus === "fetching";
    let t11;
    if ($[40] !== t10 || $[41] !== t9) {
      t11 = <div className="flex w-full gap-4"><Button appearance="filled" aria-label="Continue" className="flex-1" color="info" data-cy="sign-in" disabled={t9} isLoading={t10} type="submit">Continue</Button></div>;
      $[40] = t10;
      $[41] = t9;
      $[42] = t11;
    } else {
      t11 = $[42];
    }
    let t12;
    if ($[43] !== t11 || $[44] !== t8) {
      t12 = <div className={cardClassName}>{t6}{t8}{t11}</div>;
      $[43] = t11;
      $[44] = t8;
      $[45] = t12;
    } else {
      t12 = $[45];
    }
    let t13;
    if ($[46] !== t12 || $[47] !== t5) {
      t13 = <form className="w-full" onSubmit={t5}>{t12}</form>;
      $[46] = t12;
      $[47] = t5;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    let t14;
    if ($[49] !== backupCodeForm || $[50] !== t13) {
      t14 = <Form key="backup-code" {...backupCodeForm}>{t13}</Form>;
      $[49] = backupCodeForm;
      $[50] = t13;
      $[51] = t14;
    } else {
      t14 = $[51];
    }
    return t14;
  }
  let t5;
  if ($[52] !== codeForm || $[53] !== onSignUpSubmitCode) {
    t5 = codeForm.handleSubmit(async data_1 => {
      const ok_1 = await onSignUpSubmitCode(data_1.code);
      if (!ok_1) {
        codeForm.reset();
      }
    });
    $[52] = codeForm;
    $[53] = onSignUpSubmitCode;
    $[54] = t5;
  } else {
    t5 = $[54];
  }
  let t6;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Avatar size="48"><AvatarFallback className="bg-role-info-container text-role-on-info-container" size="48" variant="icon"><MailboxIcon size={20} /></AvatarFallback></Avatar>;
    $[55] = t6;
  } else {
    t6 = $[55];
  }
  let t7;
  if ($[56] !== signIn.identifier) {
    t7 = <div className="flex w-full flex-col items-center justify-center gap-6 text-center">{t6}<Text body="lg" className="w-full text-role-on-background">To continue, enter the code sent to{" "}<span className="wrap-break-word font-medium">{signIn.identifier}</span></Text></div>;
    $[56] = signIn.identifier;
    $[57] = t7;
  } else {
    t7 = $[57];
  }
  let t8;
  if ($[58] !== errors.fields.code) {
    t8 = t9 => {
      const {
        field: field_1
      } = t9;
      return <FormItem><FormControl><InputOTP maxLength={6} {...field_1}><InputOTPGroup className="h-full w-full"><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={0} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={1} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={2} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={3} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={4} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={5} /></InputOTPGroup></InputOTP></FormControl>{errors.fields.code ? <FormMessage>{errors.fields.code.message}</FormMessage> : <FormMessage />}</FormItem>;
    };
    $[58] = errors.fields.code;
    $[59] = t8;
  } else {
    t8 = $[59];
  }
  let t9;
  if ($[60] !== codeForm.control || $[61] !== t8) {
    t9 = <div className="flex flex-col gap-6"><FormField control={codeForm.control} name="code" render={t8} /></div>;
    $[60] = codeForm.control;
    $[61] = t8;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  const t10 = fetchStatus === "fetching" || codeForm.formState.isSubmitting || !codeForm.formState.isValid;
  const t11 = fetchStatus === "fetching";
  let t12;
  if ($[63] !== t10 || $[64] !== t11) {
    t12 = <div className="flex w-full gap-4"><Button appearance="filled" aria-label="Sign in with Email" className="flex-1" color="info" data-cy="sign-in" disabled={t10} isLoading={t11} type="submit">Continue</Button></div>;
    $[63] = t10;
    $[64] = t11;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  const t13 = resendTimeLeft > 0;
  let t14;
  if ($[66] !== onResendCode) {
    t14 = async () => {
      setResendTimeLeft(60);
      await onResendCode();
    };
    $[66] = onResendCode;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  const t15 = resendTimeLeft > 0 ? `${resendTimeLeft}s` : "60s";
  let t16;
  if ($[68] !== t15) {
    t16 = <span className="text-role-info">Resend ({t15})</span>;
    $[68] = t15;
    $[69] = t16;
  } else {
    t16 = $[69];
  }
  let t17;
  if ($[70] !== t13 || $[71] !== t14 || $[72] !== t16) {
    t17 = <Button appearance="ghost" className="gap-0" disabled={t13} onClick={t14} type="button">Didn't receive a code? {t16}</Button>;
    $[70] = t13;
    $[71] = t14;
    $[72] = t16;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== t12 || $[75] !== t17 || $[76] !== t7 || $[77] !== t9) {
    t18 = <div className={cardClassName}>{t7}{t9}{t12}{t17}</div>;
    $[74] = t12;
    $[75] = t17;
    $[76] = t7;
    $[77] = t9;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== t18 || $[80] !== t5) {
    t19 = <form className="w-full" onSubmit={t5}>{t18}</form>;
    $[79] = t18;
    $[80] = t5;
    $[81] = t19;
  } else {
    t19 = $[81];
  }
  let t20;
  if ($[82] !== codeForm || $[83] !== t19) {
    t20 = <Form key="email-code" {...codeForm}>{t19}</Form>;
    $[82] = codeForm;
    $[83] = t19;
    $[84] = t20;
  } else {
    t20 = $[84];
  }
  return t20;
}
function _temp(prev) {
  return prev - 1;
}